// src/config/guestFields.js
// ---------------------------------------------------------------------------
// What a guest is asked for on the details screen, and how each answer is
// validated and stored.
//
// **A copy of sipnscore-admin/src/config/guestFields.js.** The portal uses it to
// configure, this app uses it to render and validate, and the Cloud Function
// validates again with the same rules, because this screen is unauthenticated
// and a browser can be modified. Change one, change all three. The admin repo's
// copy has the tests.
//
// **Nothing here may break an activation that is already running.** An
// activation with no `guestFields` array gets DEFAULT_FIELDS, which is exactly
// the three questions the screen asked before any of this existed. A campaign
// that has never been opened keeps working untouched, and so does a QR code
// printed last year.
// ---------------------------------------------------------------------------

/**
 * Every field that can be asked for.
 *
 * `id` is what goes in Firestore, on the guest record and in every export.
 * Never rename one: historic guest documents carry it.
 */
export const GUEST_FIELD_TYPES = [
  {
    id: "firstName",
    label: "First name",
    inputType: "text",
    autoComplete: "given-name",
    placeholder: "Enter your first name",
    maxLength: 60,
  },
  {
    id: "lastName",
    label: "Last name",
    inputType: "text",
    autoComplete: "family-name",
    placeholder: "Enter your last name",
    maxLength: 60,
  },
  {
    id: "email",
    label: "Email address",
    inputType: "email",
    autoComplete: "email",
    placeholder: "you@example.com",
    maxLength: 160,
    // The reward link is emailed, and returning guests are matched on this
    // address. An activation with a reward pool cannot switch it off, and the
    // editor says so rather than letting somebody build a campaign that can
    // never deliver anything.
    identity: true,
  },
  {
    id: "mobile",
    label: "Mobile number",
    inputType: "tel",
    autoComplete: "tel-national",
    placeholder: "82 123 4567",
    // A phone keypad rather than a full keyboard. On iOS `tel` is the one that
    // gives the big round dial pad.
    inputMode: "tel",
    maxLength: 24,
    dialCode: true,
  },
  {
    id: "dob",
    label: "Date of birth",
    inputType: "date",
    autoComplete: "bday",
    // Native, deliberately. A phone gives its own wheel or calendar, which is
    // the friendliest thing available and needs no keyboard at all, and a
    // desktop lets somebody type the date straight in. A hand-built three
    // dropdown day/month/year is the "long ugly selector" this avoids.
    minAge: 18,
  },
];

export const FIELD_BY_ID = Object.fromEntries(
  GUEST_FIELD_TYPES.map((f) => [f.id, f]),
);

/**
 * What the screen asked before any of this was configurable.
 *
 * An activation with no stored configuration resolves to exactly this, so every
 * campaign already in the field keeps behaving as it does today.
 */
export const DEFAULT_FIELDS = [
  { id: "firstName", enabled: true, required: true },
  { id: "lastName", enabled: true, required: true },
  { id: "email", enabled: true, required: true },
];

/**
 * Turn whatever is stored into a usable, ordered list.
 *
 * Tolerant on purpose. A stored array may be missing fields added later, may
 * contain an id that no longer exists, or may be absent entirely. None of those
 * may throw on a guest's phone halfway through a scan.
 *
 * @param {Array|null|undefined} stored  the activation's own array
 * @param {Array|null|undefined} inherited  the campaign's, used when the
 *   activation has none of its own
 * @returns {Array<{id, label, enabled, required, ...definition}>} in the order
 *   the guest should see them
 */
export function resolveGuestFields(stored, inherited) {
  const source =
    (Array.isArray(stored) && stored.length && stored) ||
    (Array.isArray(inherited) && inherited.length && inherited) ||
    DEFAULT_FIELDS;

  const seen = new Set();
  const out = [];

  source.forEach((row) => {
    const def = FIELD_BY_ID[row?.id];
    // An id that is not a field any more. Skipped rather than rendered as a
    // blank input with no label.
    if (!def || seen.has(def.id)) return;
    seen.add(def.id);
    out.push({
      ...def,
      enabled: row.enabled !== false,
      required: row.required !== false,
      dialCode: row.dialCode || def.dialCodeDefault || "+27",
    });
  });

  // Anything the stored array predates is appended, switched off. A field added
  // to this file next year appears in the editor for every existing activation
  // without changing what any of them currently ask.
  GUEST_FIELD_TYPES.forEach((def) => {
    if (seen.has(def.id)) return;
    out.push({ ...def, enabled: false, required: false, dialCode: "+27" });
  });

  return out;
}

/** Just the ones a guest will actually be shown, in order. */
export const activeGuestFields = (stored, inherited) =>
  resolveGuestFields(stored, inherited).filter((f) => f.enabled);

/** The shape written back to Firestore. Only what is needed to rebuild it. */
export const toStoredGuestFields = (fields) =>
  fields.map((f) => ({
    id: f.id,
    enabled: !!f.enabled,
    required: !!f.required,
    ...(f.dialCode ? { dialCode: f.dialCode } : {}),
  }));

/* ------------------------------------------------------------- dial codes -- */

/**
 * Southern Africa first, then the rest by traffic. South Africa is the default
 * because that is where every activation so far has run.
 */
export const DIAL_CODES = [
  { code: "+27", label: "South Africa", iso: "ZA", nsnLength: 9 },
  { code: "+264", label: "Namibia", iso: "NA", nsnLength: 9 },
  { code: "+267", label: "Botswana", iso: "BW", nsnLength: 8 },
  { code: "+263", label: "Zimbabwe", iso: "ZW", nsnLength: 9 },
  { code: "+258", label: "Mozambique", iso: "MZ", nsnLength: 9 },
  { code: "+266", label: "Lesotho", iso: "LS", nsnLength: 8 },
  { code: "+268", label: "Eswatini", iso: "SZ", nsnLength: 8 },
  { code: "+260", label: "Zambia", iso: "ZM", nsnLength: 9 },
  { code: "+265", label: "Malawi", iso: "MW", nsnLength: 9 },
  { code: "+254", label: "Kenya", iso: "KE", nsnLength: 9 },
  { code: "+234", label: "Nigeria", iso: "NG", nsnLength: 10 },
  { code: "+44", label: "United Kingdom", iso: "GB", nsnLength: 10 },
  { code: "+1", label: "United States", iso: "US", nsnLength: 10 },
  { code: "+61", label: "Australia", iso: "AU", nsnLength: 9 },
  { code: "+31", label: "Netherlands", iso: "NL", nsnLength: 9 },
  { code: "+49", label: "Germany", iso: "DE", nsnLength: 10 },
  { code: "+33", label: "France", iso: "FR", nsnLength: 9 },
];

export const DEFAULT_DIAL_CODE = "+27";

/**
 * One stored format for a mobile number, whatever was typed.
 *
 * South Africans write their number four ways and all four are the same number:
 *
 *   082 123 4567     the way it is said out loud, with the trunk zero
 *   82 123 4567      the same without it
 *   +27 82 123 4567  international
 *   0821234567       no spaces
 *
 * Stored as E.164, `+27821234567`: no spaces, no punctuation, no trunk zero.
 * That is the format a bulk SMS gateway takes, it is what makes two records the
 * same person, and it is the only way a phone number is worth having in a
 * database at all. The trunk zero is dropped because it is a domestic dialling
 * prefix, not part of the number.
 *
 * @returns {{ok: boolean, value?: string, display?: string, error?: string}}
 */
export function normaliseMobile(input, dialCode = DEFAULT_DIAL_CODE) {
  const raw = String(input || "").trim();
  if (!raw) return { ok: false, error: "Mobile number is required" };

  const country =
    DIAL_CODES.find((c) => c.code === dialCode) || DIAL_CODES[0];

  // Everything that is not a digit goes, except a leading +.
  let digits = raw.replace(/[^\d+]/g, "");

  if (digits.startsWith("+")) {
    // Already international. Trust the country code in the number over the
    // picker, because somebody who typed +264 meant +264.
    const match = DIAL_CODES.find((c) => digits.startsWith(c.code));
    if (!match) return { ok: false, error: "That country code is not supported" };
    const nsn = digits.slice(match.code.length).replace(/^0+/, "");
    return finishMobile(match, nsn);
  }

  if (digits.startsWith("00")) {
    return normaliseMobile(`+${digits.slice(2)}`, dialCode);
  }

  // Domestic. A leading zero is the trunk prefix and is dropped.
  const nsn = digits.replace(/^0+/, "");
  return finishMobile(country, nsn);
}

function finishMobile(country, nsn) {
  if (!nsn) return { ok: false, error: "Enter a mobile number" };
  if (!/^\d+$/.test(nsn)) return { ok: false, error: "Numbers only, please" };

  // Length is checked against the country rather than assumed to be nine. Too
  // short is nearly always a typo; too long is a pasted extension or a double
  // country code.
  const expected = country.nsnLength;
  if (nsn.length < expected - 1 || nsn.length > expected + 1) {
    return {
      ok: false,
      error: `That does not look like a ${country.label} number`,
    };
  }

  const value = `${country.code}${nsn}`;
  return { ok: true, value, display: formatMobile(value) };
}

/** `+27821234567` back to `+27 82 123 4567`, for reading. */
export function formatMobile(e164) {
  const s = String(e164 || "");
  const country = DIAL_CODES.find((c) => s.startsWith(c.code));
  if (!country) return s;
  const nsn = s.slice(country.code.length);
  if (nsn.length === 9) {
    return `${country.code} ${nsn.slice(0, 2)} ${nsn.slice(2, 5)} ${nsn.slice(5)}`;
  }
  if (nsn.length === 10) {
    return `${country.code} ${nsn.slice(0, 3)} ${nsn.slice(3, 6)} ${nsn.slice(6)}`;
  }
  return `${country.code} ${nsn}`;
}

/* -------------------------------------------------------------------- age -- */

/**
 * Whole years between a date of birth and now.
 *
 * Compared on month and day rather than by dividing milliseconds, so somebody
 * whose birthday is today is the age they turned today and not a day short of
 * it. Leap years take care of themselves this way.
 *
 * @returns {number|null} null when the date is unreadable or in the future
 */
export function ageFrom(dob, now = new Date()) {
  if (!dob) return null;
  const d = dob instanceof Date ? dob : new Date(`${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getTime() > now.getTime()) return null;

  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
}

/** The latest date of birth that is still old enough, as `YYYY-MM-DD`. */
export function maxDobFor(minAge = 18, now = new Date()) {
  const d = new Date(now.getFullYear() - minAge, now.getMonth(), now.getDate());
  return isoDate(d);
}

/** A sensible floor for a date picker, so the wheel does not start at year 1. */
export const MIN_DOB = "1900-01-01";

export function isoDate(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/* ------------------------------------------------------------- validation -- */

/**
 * Check one answer.
 *
 * @param {object} field  a resolved field, from resolveGuestFields
 * @param {*} value       what the guest typed
 * @returns {{ok: boolean, value?: *, error?: string, blocked?: boolean}}
 *   `blocked` means the guest may not continue at all, which today is only the
 *   age check. It is different from an error: an error is fixed by typing
 *   something else, and this one is not.
 */
export function validateGuestField(field, value, now = new Date()) {
  const raw = typeof value === "string" ? value.trim() : value;

  if (!raw) {
    return field.required
      ? { ok: false, error: `${field.label} is required` }
      : { ok: true, value: null };
  }

  if (field.id === "email") {
    const email = String(raw).toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { ok: false, error: "That email address does not look right" };
    }
    return { ok: true, value: email };
  }

  if (field.id === "mobile") {
    const r = normaliseMobile(raw, field.dialCode);
    return r.ok ? { ok: true, value: r.value } : { ok: false, error: r.error };
  }

  if (field.id === "dob") {
    const age = ageFrom(raw, now);
    if (age === null) return { ok: false, error: "Enter a valid date of birth" };
    if (age > 120) return { ok: false, error: "Enter a valid date of birth" };
    const minAge = field.minAge || 0;
    if (minAge && age < minAge) {
      return {
        ok: false,
        blocked: true,
        error: `You need to be ${minAge} or older to take part.`,
      };
    }
    return { ok: true, value: String(raw) };
  }

  return { ok: true, value: String(raw).slice(0, field.maxLength || 200) };
}

/** Validate a whole form. Returns errors keyed by field id. */
export function validateGuestForm(fields, values, now = new Date()) {
  const errors = {};
  const clean = {};
  let blocked = null;

  fields
    .filter((f) => f.enabled)
    .forEach((f) => {
      const r = validateGuestField(f, values[f.id], now);
      if (r.ok) {
        if (r.value !== null && r.value !== undefined) clean[f.id] = r.value;
      } else {
        errors[f.id] = r.error;
        if (r.blocked) blocked = r.error;
      }
    });

  return { ok: Object.keys(errors).length === 0, errors, clean, blocked };
}
